import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'genzox_session_token';
const REFRESH_KEY = 'genzox_refresh_token';

// iter128 — Native storage bridge for iOS/Android shells.
//
// On iOS WKWebView we occasionally saw the persistent-login flow break
// because the WebView data store was recreated between app launches
// (both localStorage AND cookies get wiped). If the native shell injects
// a `window.__genzoxNativeStorage` object with `getItem(key)` /
// `setItem(key, val)` / `removeItem(key)` — typically backed by Keychain
// on iOS and EncryptedSharedPreferences on Android — we prefer that
// store for the two auth tokens so tokens survive WebView resets. Falls
// back to localStorage for the web build (bridge is undefined).
const _nativeStorage = () => {
  try {
    const ns = typeof window !== 'undefined' ? window.__genzoxNativeStorage : null;
    if (ns && typeof ns.getItem === 'function' && typeof ns.setItem === 'function') return ns;
  } catch (_) { /* SSR or locked-down env */ }
  return null;
};

const _readAuthValue = (key) => {
  const ns = _nativeStorage();
  if (ns) {
    try {
      const v = ns.getItem(key);
      if (v) return v;
    } catch (_) { /* fall through to localStorage */ }
  }
  try { return localStorage.getItem(key) || ''; } catch (_) { return ''; }
};

const _writeAuthValue = (key, value) => {
  const ns = _nativeStorage();
  // Always mirror to localStorage so JS in-tab reads stay fast. When a
  // native bridge is present we also push to it so the shell can back
  // it with Keychain / EncryptedSharedPreferences.
  try {
    if (value) localStorage.setItem(key, value);
    else localStorage.removeItem(key);
  } catch (_) { /* private mode */ }
  if (ns) {
    try {
      if (value) ns.setItem(key, value);
      else if (typeof ns.removeItem === 'function') ns.removeItem(key);
      else ns.setItem(key, '');
    } catch (_) { /* best-effort */ }
  }
};

export const getStoredToken = () => _readAuthValue(TOKEN_KEY);
export const setStoredToken = (token) => _writeAuthValue(TOKEN_KEY, token || '');
// iter120 — Refresh token persistence for indefinite login. Mobile shells
// and PWAs pull this from localStorage; native wrappers can also mirror
// it into Keychain / Android Keystore via the __genzoxNativeStorage bridge.
export const getStoredRefreshToken = () => _readAuthValue(REFRESH_KEY);
export const setStoredRefreshToken = (token) => _writeAuthValue(REFRESH_KEY, token || '');

const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true, // belt-and-suspenders: keep cookies AND headers
  // iter141 (Phase C) — Uniform axios timeout so a stalled TCP connection on
  // flaky mobile data can't leave a UI stuck spinning forever. 30 s covers
  // every legitimate long call (image upload, plan purchase, chat history)
  // while still surfacing genuine hangs as an axios timeout the caller can
  // recover from with a clear "Network slow, please retry" toast. Individual
  // upload endpoints can pass `{ timeout: N }` to override when needed.
  timeout: 30000,
});

// Attach Bearer token from localStorage on every request — survives CORS edge cases
api.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// iter120 — Auto-persist refresh_token whenever an /auth/* response includes one.
// Avoids having to touch each of the 8 setStoredToken call-sites.
// iter128 — Narrowed to /auth/* routes only per code review: if any other
// endpoint ever returns a stray `refresh_token` field (mistakenly or via
// a compromised path) it should NOT auto-overwrite the real refresh token.
api.interceptors.response.use(
  (res) => {
    try {
      const url = (res && res.config && res.config.url) || '';
      if (url.startsWith('/auth/') || url.includes('/api/auth/')) {
        const rt = res && res.data && res.data.refresh_token;
        if (rt) setStoredRefreshToken(rt);
      }
    } catch (_) { /* noop */ }
    return res;
  },
  // Preserve any pre-existing error handlers by re-throwing here; the
  // dedicated 401-refresh interceptor below handles error responses.
  (err) => Promise.reject(err),
);

// iter120 — Response interceptor: on 401 from any authenticated call, try to
// silently mint a new session via the refresh token, update local storage,
// and retry the original request exactly once. If refresh fails, the app's
// AuthContext will see the second 401 and route the user to /login.
//
// `_retried` flag on the config prevents infinite loops (401 on /auth/refresh
// itself, or a second 401 after a fresh session token).
let _refreshInFlight = null;
api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const cfg = err.config;
    if (!err.response || err.response.status !== 401 || !cfg || cfg._retried) {
      return Promise.reject(err);
    }
    // Never try to refresh the refresh endpoint or the primary logout call.
    const url = cfg.url || '';
    if (url.includes('/auth/refresh') || url.includes('/auth/logout')) {
      return Promise.reject(err);
    }
    const rt = getStoredRefreshToken();
    if (!rt) return Promise.reject(err);
    cfg._retried = true;
    try {
      // Coalesce concurrent refresh attempts into a single request.
      if (!_refreshInFlight) {
        _refreshInFlight = axios.post(
          `${API_URL}/api/auth/refresh`,
          { refresh_token: rt },
          { withCredentials: true },
        ).finally(() => { _refreshInFlight = null; });
      }
      const res = await _refreshInFlight;
      const newSess = res?.data?.session_token;
      const newRefresh = res?.data?.refresh_token;
      if (newSess) setStoredToken(newSess);
      if (newRefresh) setStoredRefreshToken(newRefresh);
      // Retry original with the fresh Bearer token attached.
      cfg.headers = cfg.headers || {};
      if (newSess) cfg.headers.Authorization = `Bearer ${newSess}`;
      return api.request(cfg);
    } catch (refreshErr) {
      // Refresh failed → wipe stored tokens so the next page render kicks
      // the user cleanly to /login instead of looping.
      setStoredRefreshToken('');
      return Promise.reject(err);
    }
  },
);

export const authAPI = {
  createSession: (sessionId) => api.post('/auth/session', { session_id: sessionId }),
  getMe: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  sendOTP: (phoneNumber) => api.post('/auth/send-otp', { phone_number: phoneNumber }),
  checkPhone: (phoneNumber) => api.post('/auth/check-phone', { phone_number: phoneNumber }),
  verifyOTP: (phoneNumber, code, name, email, mergeWithExistingEmailAccount) => api.post('/auth/verify-otp', { phone_number: phoneNumber, code, name: name || undefined, email: email || undefined, merge_with_existing_email_account: mergeWithExistingEmailAccount ?? undefined }),
  verifyAadhaar: (aadhaarNumber, otp) => api.post('/auth/verify-aadhaar', { aadhaar_number: aadhaarNumber, otp }),
  updateEmailPreferences: (optOut) => api.put('/users/me/email-preferences', { expiry_email_opt_out: optOut }),
  updateProfile: (fields) => api.put('/auth/update-profile', fields),
  updateBusinessName: (businessName, alternatePhone) => api.put('/auth/business-name', {
    business_name: businessName,
    ...(alternatePhone !== undefined ? { alternate_phone: alternatePhone } : {}),
  }),
  sendEmailOTP: (email) => api.post('/auth/email/send-otp', { email }),
  verifyEmailOTP: (email, code) => api.post('/auth/email/verify-otp', { email, code }),
  // iter104e — "Continue with Email" login flow. Anonymous — sends a 6-digit
  // OTP to email, verify creates/logs in a user keyed by email.
  emailLoginSendOTP: (email) => api.post('/auth/email-login/send-otp', { email }),
  emailLoginVerifyOTP: (email, code, name) =>
    api.post('/auth/email-login/verify-otp', { email, code, name: name || undefined }),
  // App Store Guideline 5.1.1(v) — self-service account deletion.
  // Returns { status:'ok', message, deleted_user_id, stats:{listings_removed, conversations_removed} }
  deleteAccount: () => api.delete('/account')
};

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

// iter119 — Silent retry-once for the OAuth verify endpoints. When the
// backend times-out its verification (503 → we mapped from `asyncio.TimeoutError`
// on Google-cert / Apple-JWKS fetches) or when Cloudflare returns a transient
// 502/504 / network error, we retry the exact same call once after a short
// back-off. This means users almost never see the "verification temporarily
// unavailable" surface — the mobile shell recovers on its own.
const _isTransientAuthError = (err) => {
  if (!err) return false;
  const s = err.response?.status;
  if (s === 503 || s === 502 || s === 504) return true;
  // Cloudflare 520 / 521 / 522 / 524 all indicate origin problems.
  if (s === 520 || s === 521 || s === 522 || s === 524) return true;
  // No response at all → network error (offline, DNS blip, TLS reset).
  if (!err.response && err.request) return true;
  return false;
};
const _retryOnce = async (call) => {
  try { return await call(); }
  catch (err) {
    if (!_isTransientAuthError(err)) throw err;
    // Short back-off — enough time for JWKS/certs to cache-warm on the pod
    // that handled the first request, or for Cloudflare to route to a
    // different, healthier upstream replica.
    await new Promise((r) => setTimeout(r, 900));
    return call();
  }
};

export const googleExchange = (code, redirectUri, opts = {}) =>
  _retryOnce(() => api.post('/auth/google/exchange', {
    code,
    redirect_uri: redirectUri,
    // iter131 — see appleNativeExchange: must be `true` only from the
    // EditProfile "Link Google Account" flow. Login page never sets it.
    link: !!opts.link,
  }));

// Native Google Sign-In: WebToNative SDK returns an id_token directly from
// the native Google Sign-In flow (Android GoogleSignInClient / iOS GIDSignIn).
// We POST the id_token to the backend which verifies it against our
// GOOGLE_CLIENT_ID and creates a session — no code-exchange step.
export const googleNativeExchange = (idToken, opts = {}) =>
  _retryOnce(() => api.post('/auth/google/native', { id_token: idToken, link: !!opts.link }));

// Native Apple Sign-In: WebToNative SDK returns an id_token + (first-time only)
// first/last name + email. Backend verifies the JWT against Apple's JWKs and
// our APPLE_BUNDLE_ID, then creates a session. Only fires inside iOS native app.
export const appleNativeExchange = (idToken, profile = {}, opts = {}) =>
  _retryOnce(() => api.post('/auth/apple/native', {
    id_token: idToken,
    first_name: profile.firstName || null,
    last_name: profile.lastName || null,
    email: profile.emailId || null,
    // iter131 — must be `true` ONLY when explicitly linking Apple to the
    // already-logged-in user from Account Settings. Default false so the
    // Login page's "Continue with Apple" cannot ever silently link Apple
    // to a stale-cookie account.
    link: !!opts.link,
  }));

// iter131 — Disconnect Apple Sign-In from the current user (recovery path
// for anyone hit by the pre-iter131 silent-linking bug).
export const appleUnlink = () => api.post('/auth/apple/unlink');

export const categoriesAPI = {
  getAll: () => api.get('/categories')
};

export const listingsAPI = {
  getAll: (params) => api.get('/listings', { params }),  getById: (id) => api.get(`/listings/${id}`),
  getNearby: (params) => api.get('/listings/nearby', { params }),
  suggest: (q) => api.get('/listings/suggest', { params: { q } }),
  getMy: () => api.get('/listings/my'),
  create: (data) => api.post('/listings', data),
  update: (listingId, data) => api.put(`/listings/${listingId}`, data),
  uploadImage: (listingId, file, onUploadProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/listings/${listingId}/upload-image`, formData, {
      onUploadProgress,
      // Override the global 30 s axios timeout — image uploads on 2G/3G
      // can legitimately take 60-90 s for large photos.
      timeout: 120000,
    });
  },
  deleteImage: (listingId, imageIndex) => api.delete(`/listings/${listingId}/images/${imageIndex}`),
  reorderImages: (listingId, imageOrder) => api.put(`/listings/${listingId}/images/reorder`, { image_order: imageOrder }),
  setCoverImage: (listingId, imageIndex) => api.put(`/listings/${listingId}/cover-image/${imageIndex}`),
  publish: (listingId) => api.put(`/listings/${listingId}/publish`),
  resubmit: (listingId) => api.put(`/listings/${listingId}/resubmit`),
  reactivate: (listingId) => api.post(`/listings/${listingId}/reactivate`),
  markSold: (listingId) => api.post(`/listings/${listingId}/mark-sold`),
  markActive: (listingId) => api.post(`/listings/${listingId}/mark-active`),
  delete: (listingId) => api.delete(`/listings/${listingId}`)
};

export const subscriptionsAPI = {
  getPlans: () => api.get('/subscriptions/plans'),
  getDealerPlans: () => api.get('/subscriptions/dealer-plans'),
  purchase: (planType, razorpayOrderId) => api.post('/subscriptions/purchase', { plan_type: planType, razorpay_order_id: razorpayOrderId }),
  getMy: () => api.get('/subscriptions/my')
};

export const addonsAPI = {
  getCatalog: () => api.get('/addons/catalog'),
  applyToListing: (listingId, addonId, paymentRefs = {}) => api.post(`/listings/${listingId}/apply-addon`, {
    addon_id: addonId,
    listing_id: listingId,
    razorpay_order_id: paymentRefs.razorpay_order_id,
    razorpay_payment_id: paymentRefs.razorpay_payment_id,
  }),
};

export const paymentsAPI = {
  createOrder: (amount, purpose, intent = {}) => api.post('/payments/create-order', { amount, purpose, ...intent }),
  verify: (data) => api.post('/payments/verify', data),
  status: (orderId) => api.get(`/payments/status/${orderId}`),
};

// Apple In-App Purchase (iOS-only). Web/Android continue to use Razorpay.
// The native WebToNative SDK returns a base64 receipt → POST it here so the
// backend can call Apple's verifyReceipt and grant the entitlement.
export const iapAPI = {
  getProducts: () => api.get('/iap/apple/products'),
  verify: (payload) => api.post('/iap/apple/verify', payload),
  restore: (payload) => api.post('/iap/apple/restore', payload),
};

export const wishlistAPI = {
  add: (listingId) => api.post(`/wishlist/${listingId}`),
  remove: (listingId) => api.delete(`/wishlist/${listingId}`),
  getAll: () => api.get('/wishlist'),
  // iter114 — called when the user opens a wishlisted listing detail so the
  // "Price Dropped / Price Increased" badge disappears on next wishlist load.
  markSeen: (listingId) => api.post(`/wishlist/${listingId}/seen`),
};

export const chatAPI = {
  getConversations: () => api.get('/conversations'),
  getUnreadCount: () => api.get('/conversations/unread-count'),
  getConversation: (conversationId) => api.get(`/conversations/${conversationId}`),
  startConversation: (data) => api.post('/conversations/start', data),
  getMessages: (conversationId) => api.get(`/messages/${conversationId}`),
  sendMessage: (data) => api.post('/messages', data),
  markTyping: (conversationId) => api.post(`/conversations/${conversationId}/typing`),
  markRead: (conversationId) => api.post(`/conversations/${conversationId}/read`),
  getState: (conversationId) => api.get(`/conversations/${conversationId}/state`),
  deleteConversation: (conversationId) => api.delete(`/conversations/${conversationId}`),
};

export const reportsAPI = {
  create: (data) => api.post('/reports', data),
  blockUser: (userId) => api.post(`/block/${userId}`)
};

export const reviewsAPI = {
  create: (data) => api.post('/reviews', data),
  getSellerReviews: (sellerId) => api.get(`/reviews/seller/${sellerId}`)
};

export const usersAPI = {
  getPublic: (userId) => api.get(`/users/${userId}/public`),
  getListings: (userId) => api.get('/listings', { params: { user_id: userId } })
};

export const locationsAPI = {
  search: (q) => api.get('/locations/search', { params: { q } })
};

export const bannersAPI = {
  list: (position = 'homepage_hero') => api.get('/banners', { params: { position } })
};

// Public site-wide settings (currently just the hero autoplay interval).
export const settingsAPI = {
  get: () => api.get('/settings'),
};

export const adminAPI = {
  getUsers: () => api.get('/admin/users'),  searchUsers: (params) => api.get('/admin/users/search', { params }),
  toggleBlockUser: (userId, blocked) => api.put(`/admin/users/${userId}/block`, null, { params: { blocked } }),
  setUserRole: (userId, isAdmin) => api.put(`/admin/users/${userId}/role`, null, { params: { is_admin: isAdmin } }),
  updateUser: (userId, fields) => api.put(`/admin/users/${userId}`, fields),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  getUserFull: (userId) => api.get(`/admin/users/${userId}/full`),
  getListings: () => api.get('/admin/listings'),
  deleteListing: (listingId) => api.delete(`/admin/listings/${listingId}`),
  approveListing: (listingId) => api.put(`/admin/listings/${listingId}/approve`),
  rejectListing: (listingId, reasons) => api.put(`/admin/listings/${listingId}/reject?reasons=${encodeURIComponent(reasons)}`),
  respondListing: (listingId, payload) => api.post(`/admin/listings/${listingId}/respond`, payload),
  forcePro: (listingId, durationDays = 30) => api.put(`/admin/listings/${listingId}/force-pro`, { duration_days: durationDays }),
  forceBoost: (listingId, durationDays = 7) => api.put(`/admin/listings/${listingId}/force-boost`, { duration_days: durationDays }),
  hideListing: (listingId, hide = true) => api.put(`/admin/listings/${listingId}/hide`, null, { params: { hide } }),
  bulkApprove: (listingIds) => api.post('/admin/listings/bulk-approve', { listing_ids: listingIds }),
  bulkReject: (listingIds, reasons = 'Policy violation') => api.post('/admin/listings/bulk-reject', { listing_ids: listingIds, reasons }),
  bulkDelete: (listingIds, hardDelete = false) => api.post('/admin/listings/bulk-delete', { listing_ids: listingIds, hard_delete: hardDelete }),
  updateListing: (listingId, fields) => api.put(`/admin/listings/${listingId}`, fields),
  getPayments: (params) => api.get('/admin/payments', { params }),
  getSubscriptions: (activeOnly = false) => api.get('/admin/subscriptions', { params: { active_only: activeOnly } }),
  grantSubscription: (userId, planType, note = '', isLifetime = false) =>
    api.post('/admin/subscriptions/grant', { user_id: userId, plan_type: planType, note, is_lifetime: isLifetime }),
  // iter77 — Revoke any active subscription (including admin-granted lifetime).
  deactivateSubscription: (subscriptionId, note = '') =>
    api.post(`/admin/subscriptions/${subscriptionId}/deactivate`, null, { params: { note } }),
  refundPayment: (paymentId, note = '') => api.put(`/admin/payments/${paymentId}/refund`, null, { params: { note } }),
  listBanners: () => api.get('/admin/banners'),
  createBanner: (data) => api.post('/admin/banners', data),
  updateBanner: (id, data) => api.put(`/admin/banners/${id}`, data),
  deleteBanner: (id) => api.delete(`/admin/banners/${id}`),
  uploadBannerImage: (file) => { const fd = new FormData(); fd.append('file', file); return api.post('/admin/banners/upload-image', fd, { timeout: 120000 }); },
  updateSettings: (data) => api.put('/admin/settings', data),
  // iter104 — Duplicate-email account merger.
  mergeDuplicateEmails: (email, commit = false) =>
    api.post('/admin/merge-duplicate-emails', null, { params: { email, commit } }),
  analyticsTimeseries: (days = 30) => api.get('/admin/analytics/timeseries', { params: { days } }),
  analyticsTopCategories: () => api.get('/admin/analytics/top-categories'),
  analyticsRevenueByPlan: () => api.get('/admin/analytics/revenue-by-plan'),
  exportUsersCsv: () => `${process.env.REACT_APP_BACKEND_URL}/api/admin/export/users.csv`,
  exportListingsCsv: () => `${process.env.REACT_APP_BACKEND_URL}/api/admin/export/listings.csv`,
  emailBroadcast: (subject, htmlBody, segment = 'all', confirmToken = '') => api.post('/admin/email-broadcast', { subject, html_body: htmlBody, segment, confirm_token: confirmToken }),
  getAuditLog: (limit = 200) => api.get('/admin/audit-log', { params: { limit } }),
  createAnnouncement: (title, body, url = '/') => api.post('/admin/push-announcement', { title, body, url }),
  deleteAnnouncement: (id) => api.delete(`/admin/announcements/${id}`),
  globalSearch: (q) => api.get('/admin/search', { params: { q } }),
  getPendingReviews: () => api.get('/admin/pending-reviews'),
  getModerationLogs: () => api.get('/admin/moderation-logs'),
  getStats: () => api.get('/admin/stats'),
  getReports: () => api.get('/admin/reports'),
  updateReport: (reportId, status) => api.put(`/admin/reports/${reportId}?status=${status}`),
  getSuspiciousUsers: () => api.get('/admin/suspicious-users'),
  getEmailStatus: () => api.get('/admin/email-status'),
  sendTestEmail: (toEmail) => api.post('/admin/send-test-email', { to_email: toEmail }),
  getCoupons: () => api.get('/admin/coupons'),
  createCoupon: (data) => api.post('/admin/coupons', data),
  deleteCoupon: (id) => api.delete(`/admin/coupons/${id}`),
  // Dealer management
  listDealers: (params = {}) => api.get('/admin/dealers', { params }),
  updateDealer: (userId, fields) => api.put(`/admin/dealers/${userId}`, fields),
  grantDealer: (userId, dealerCategoryId, businessName = '', verified = true) =>
    api.post(`/admin/dealers/${userId}/grant`, { dealer_category_id: dealerCategoryId, business_name: businessName, dealer_verified: verified }),
  revokeDealer: (userId) => api.post(`/admin/dealers/${userId}/revoke`),
  // iter63 — Admin push notifications
  pushStats: () => api.get('/admin/push/stats'),
  pushHistory: () => api.get('/admin/push/history'),
  pushBroadcast: (title, body, url = '/', target = 'all') => api.post('/admin/push/broadcast', { title, body, url, target }),
  // AdSense config (Homepage ads)
  getAdSenseConfig: () => api.get('/admin/adsense'),
  updateAdSenseConfig: (data) => api.put('/admin/adsense', data)
};

// Public Homepage AdSense config
export const adsenseAPI = {
  getConfig: () => api.get('/adsense/config'),
};

export const couponsAPI = {
  validate: (code) => api.post(`/coupons/validate?code=${encodeURIComponent(code)}`),
  apply: (code) => api.post(`/coupons/apply?code=${encodeURIComponent(code)}`)
};

// iter59 — Contact Us form
export const contactAPI = {
  submit: (payload) => api.post('/contact', payload),
  adminList: (status) => api.get('/admin/contact', { params: status ? { status } : {} }),
  adminUpdateStatus: (messageId, status) => api.patch(`/admin/contact/${messageId}`, { status }),
  adminDelete: (messageId) => api.delete(`/admin/contact/${messageId}`),
  adminReply: (messageId, reply) => api.post(`/admin/contact/${messageId}/reply`, { reply }),
};

// iter60 — Admin chat visibility (read-only)
export const adminChatAPI = {
  listConversations: (params) => api.get('/admin/chat/conversations', { params: params || {} }),
  getConversationMessages: (conversationId) => api.get(`/admin/chat/conversations/${conversationId}/messages`),
};

// iter67 — WebToNative FCM device-token registration
export const devicesAPI = {
  register: (token, platform, opts = {}) => api.post('/devices/register', {
    token,
    platform,
    app_version: opts.app_version,
    device_model: opts.device_model,
  }),
  unregister: (token) => api.delete('/devices/register', { params: { token } }),
};

export default api;
